// usePwaInstalar — CUÁNDO y CÓMO invitar a instalar la app en el celular (round 2026-10-29).
//
// Reúne las tres piezas (plataforma, evento de instalación de Android, memoria del dispositivo) y devuelve lo único que el
// componente necesita: si se ve, en qué modo y qué hace cada botón. Las reglas, todas juntas:
//
//   1. Solo celulares y tablets táctiles (`plataforma.esMovil`). En escritorio no se invita nunca.
//   2. Nunca si la app ya corre instalada (`getDisplayMode() === "standalone"`).
//   3. Solo con sesión iniciada y fuera de las pantallas públicas: eso lo decide quien llama con `habilitado` (el componente
//      lo apaga en /login y en la recuperación de contraseña, para no tapar el formulario ni el widget de Turnstile).
//   4. No al instante: unos segundos después de quedar habilitado (la persona entró para hacer algo, no para esto).
//   5. Una vez por sesión del navegador como máximo (sessionStorage), aunque se cierre sesión y se entre de nuevo.
//   6. Sin silencio guardado en este dispositivo («Ahora no» 14 días, «No volver a mostrar» y «Ya la instalé» para siempre).
//   7. Android: solo si Chrome entregó `beforeinstallprompt`. Sin evento NO se muestra nada: puede estar ya instalada (y no
//      hay forma de saberlo), o ser un navegador sin instalación (Firefox), y no hay que molestar. Si el evento llega
//      DESPUÉS de la espera, el aviso aparece en ese momento.
//   8. iPhone/iPad: no hay evento ni API; se muestran instrucciones según el navegador (ver utils/plataformaInstalacion.ts).
//
// «Una vez» se cumple marcando la sesión en el momento en que el aviso SE MUESTRA (no cuando se descarta): si la persona lo
// ignora y recarga la página, no vuelve a salir en esa pestaña.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  IOS_MINIMA_OTROS_NAVEGADORES,
  leerPlataformaActual,
  type PlataformaInstalacion,
} from "../utils/plataformaInstalacion";
import { leerEstadoInstalacion, solicitarInstalacion, suscribirInstalacion } from "../utils/instalacionPwa";
import {
  avisoSilenciado,
  marcarSesionMostrada,
  posponerAviso,
  sesionYaMostrada,
  silenciarParaSiempre,
} from "../utils/memoriaInstalacionPwa";
import { getDisplayMode } from "../utils/displayMode";

/** Espera entre quedar habilitado y mostrar el aviso. */
export const RETARDO_AVISO_PWA_MS = 6000;

/**
 * Cuánto se espera, como máximo, la respuesta del diálogo de instalación del sistema. `prompt()` o `userChoice` pueden no
 * resolverse nunca (el diálogo se perdió, la pestaña pasó a segundo plano, un WebView que no lo implementa bien) y sin tope la
 * hoja quedaba con el botón en carga y «Ahora no» / «No volver a mostrar» deshabilitados, sin salida. Un diálogo del sistema
 * que de verdad está abierto lo contesta una persona en segundos: 60 s de silencio es un diálogo perdido, no una decisión.
 */
export const ESPERA_MAXIMA_INSTALACION_MS = 60_000;

export type ModoAvisoPwa = "android" | "ios-safari" | "ios-otro" | "ios-embebido";

export interface OpcionesPwaInstalar {
  /** Sesión iniciada y pantalla permitida. */
  habilitado: boolean;
  retardoMs?: number;
}

export interface PwaInstalar {
  visible: boolean;
  modo: ModoAvisoPwa | null;
  /** iOS con un navegador que no es Safari y una versión que quizá no lo permita: agregar «ábrela en Safari». */
  notaSafari: boolean;
  /** Nombre de la app que embebe el navegador (Instagram…), si se reconoció. */
  appEmbebida: string | null;
  /** Hay un diálogo de instalación del sistema abierto: los botones esperan. */
  instalando: boolean;
  instalar: () => Promise<void>;
  /** «Ahora no» y también cerrar con la X o con Esc: 14 días sin aviso. */
  ahoraNo: () => void;
  noVolverAMostrar: () => void;
  /** Solo iOS: la persona ya la instaló (no hay forma de saberlo desde la página). */
  yaLaInstale: () => void;
}

/** Qué se muestra en cada plataforma. `null` = nada. */
export function modoDeAviso(plataforma: PlataformaInstalacion, hayEventoDeInstalacion: boolean): ModoAvisoPwa | null {
  if (!plataforma.esMovil) return null;
  if (plataforma.sistema === "android") return hayEventoDeInstalacion ? "android" : null;
  if (plataforma.sistema === "ios") {
    if (plataforma.navegadorIos === "embebido") return "ios-embebido";
    return plataforma.navegadorIos === "safari" ? "ios-safari" : "ios-otro";
  }
  return null;
}

export function usePwaInstalar({ habilitado, retardoMs = RETARDO_AVISO_PWA_MS }: OpcionesPwaInstalar): PwaInstalar {
  const plataforma = useMemo(() => leerPlataformaActual(), []);
  const instalacion = useSyncExternalStore(suscribirInstalacion, leerEstadoInstalacion, leerEstadoInstalacion);
  const standalone = useMemo(() => getDisplayMode() === "standalone", []);

  const [espera, setEspera] = useState(false);
  const [mostrado, setMostrado] = useState(false);
  const [cerrado, setCerrado] = useState(false);
  const [instalando, setInstalando] = useState(false);
  // Temporizador de seguridad de `instalar`: se limpia al terminar y al desmontar (no deja un timer vivo ni un setState tardío).
  const temporizadorInstalacion = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limpiarTemporizadorInstalacion = useCallback(() => {
    if (temporizadorInstalacion.current !== null) clearTimeout(temporizadorInstalacion.current);
    temporizadorInstalacion.current = null;
  }, []);
  useEffect(() => limpiarTemporizadorInstalacion, [limpiarTemporizadorInstalacion]);

  const puedeEsperar = habilitado && plataforma.esMovil && !standalone && !cerrado;

  // La espera arranca al quedar habilitado y se reinicia si se sale de las pantallas permitidas (cerrar sesión, ir al login).
  useEffect(() => {
    if (!puedeEsperar) {
      setEspera(false);
      setMostrado(false);
      return;
    }
    const t = setTimeout(() => setEspera(true), retardoMs);
    return () => clearTimeout(t);
  }, [puedeEsperar, retardoMs]);

  const modo = modoDeAviso(plataforma, instalacion.disponible);
  const visible =
    puedeEsperar &&
    espera &&
    modo !== null &&
    !instalacion.instalada &&
    !avisoSilenciado() &&
    (mostrado || !sesionYaMostrada());

  // «Una vez por sesión»: se marca al mostrarse.
  useEffect(() => {
    if (visible && !mostrado) {
      marcarSesionMostrada();
      setMostrado(true);
    }
  }, [visible, mostrado]);

  const ahoraNo = useCallback(() => {
    posponerAviso();
    setCerrado(true);
  }, []);
  const noVolverAMostrar = useCallback(() => {
    silenciarParaSiempre("no-mostrar");
    setCerrado(true);
  }, []);
  const yaLaInstale = useCallback(() => {
    silenciarParaSiempre("ya-instalada");
    setCerrado(true);
  }, []);
  const instalar = useCallback(async () => {
    if (instalando) return;
    setInstalando(true);
    // Si el diálogo del sistema no contesta en ESPERA_MAXIMA_INSTALACION_MS, se deja de esperar (ver la constante).
    const vencida = new Promise<"vencida">((resolver) => {
      temporizadorInstalacion.current = setTimeout(() => resolver("vencida"), ESPERA_MAXIMA_INSTALACION_MS);
    });
    // `solicitarInstalacion` llama a `prompt()` de forma síncrona: sigue dentro del gesto del clic.
    const resultado = await Promise.race([solicitarInstalacion(), vencida]);
    limpiarTemporizadorInstalacion();
    if (resultado === "vencida") {
      // Sin respuesta no hay decisión: no se silencia ni se pospone nada y la hoja sigue abierta con todos sus botones. El
      // evento de instalación ya se gastó en el `prompt()` que no contestó, así que un segundo «Instalar» solo cierra la hoja
      // (`no-disponible`); «Ahora no» y «No volver a mostrar» funcionan como siempre.
      setInstalando(false);
      return;
    }
    if (resultado === "accepted") silenciarParaSiempre("instalada");
    else if (resultado === "dismissed") posponerAviso();
    setInstalando(false);
    setCerrado(true);
  }, [instalando, limpiarTemporizadorInstalacion]);

  return {
    visible,
    modo,
    notaSafari:
      modo === "ios-otro" && (plataforma.versionIos === null || plataforma.versionIos < IOS_MINIMA_OTROS_NAVEGADORES),
    appEmbebida: plataforma.appEmbebida,
    instalando,
    instalar,
    ahoraNo,
    noVolverAMostrar,
    yaLaInstale,
  };
}
